import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";
import { UseCaseExamples } from "@/components/UseCaseExamples";
import { getUseCase, resolveUseCaseSymbols, useCases, USE_CASE_REDIRECTS, type UseCaseLink } from "@/data/use-cases";

export const Route = createFileRoute("/use-cases/$slug")({
  loader: ({ params }) => {
    // Old, now-repurposed use-case slugs 301-redirect to the category that
    // replaced them, so already-indexed URLs don't 404.
    const moved = USE_CASE_REDIRECTS[params.slug];
    if (moved) throw redirect({ to: "/categories/$slug", params: { slug: moved }, statusCode: 301 });
    const uc = getUseCase(params.slug);
    if (!uc) throw notFound();
    return { uc, indexed: resolveUseCaseSymbols(uc) };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { uc } = loaderData;
    const title = `${uc.name} — copy & paste, ${uc.examples.length} in-context examples`;
    return {
      meta: [
        { title },
        { name: "description", content: uc.tagline },
        { property: "og:title", content: uc.name },
        { property: "og:description", content: uc.tagline },
        { property: "og:url", content: abs(`/use-cases/${params.slug}`) },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: abs(`/use-cases/${params.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: uc.name,
            description: uc.tagline,
            url: `/use-cases/${params.slug}`,
            numberOfItems: uc.symbols.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "Use cases", item: abs("/use-cases") },
              { "@type": "ListItem", position: 3, name: uc.name, item: abs(`/use-cases/${params.slug}`) },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Use case not found</h1>
          <Link to="/use-cases" className="mt-4 inline-block text-accent hover:underline">
            ← Browse all use cases
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: UseCasePage,
});

function RelatedChip({ link }: { link: UseCaseLink }) {
  const cls =
    "glass inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs transition-colors hover:border-primary/40";
  if (link.kind === "category") {
    return (
      <Link to="/categories/$slug" params={{ slug: link.slug }} className={cls}>
        <span className="text-muted-foreground">Category:</span>
        <span className="font-medium">{link.label}</span>
      </Link>
    );
  }
  return (
    <Link to="/scripts/$slug" params={{ slug: link.slug }} className={cls}>
      <span className="text-muted-foreground">Script:</span>
      <span className="font-medium">{link.label}</span>
    </Link>
  );
}

function UseCasePage() {
  const { uc, indexed } = Route.useLoaderData();
  const others = useCases.filter((u) => u.slug !== uc.slug).slice(0, 6);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/use-cases" className="hover:text-foreground">Use cases</Link>
            <span>/</span>
            <span className="text-foreground">{uc.name}</span>
          </nav>

          <h1 className="font-display text-3xl font-bold sm:text-5xl">{uc.name}</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {uc.tagline}
          </p>

          <div className="prose prose-invert mt-5 max-w-3xl space-y-4 text-foreground/90">
            {uc.intro.map((p: string, i: number) => (
              <p key={i} className="text-[15px] leading-relaxed">
                {p}
              </p>
            ))}
          </div>

          {uc.related.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {uc.related.map((r: UseCaseLink) => (
                <RelatedChip key={`${r.kind}:${r.slug}`} link={r} />
              ))}
            </div>
          )}
        </section>

        {/* In-context examples — the heart of the page. */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-10 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">Examples — tap any line to copy</h2>
          <UseCaseExamples examples={uc.examples} />
        </section>

        {/* The individual glyphs used, grabbable one at a time. */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">Symbols used here</h2>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {indexed.map((s: { symbol: string; slug: string; name: string }) => (
              <li key={s.slug}>
                <Link
                  to="/symbol/$slug"
                  params={{ slug: s.slug }}
                  className="glass flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:border-primary/40"
                  title={`${s.name} — copy ${s.symbol}`}
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                    {s.symbol}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{s.name}</span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              </li>
            ))}
          </ul>
          {indexed.length < uc.symbols.length && (
            <p className="mt-4 text-xs text-muted-foreground">
              {uc.symbols.length - indexed.length} symbol(s) in this set live
              outside the indexed catalog — paste-only, no detail page:{" "}
              <span className="font-mono text-foreground">
                {uc.symbols.filter((s: string) => !indexed.find((i: { symbol: string }) => i.symbol === s)).join(" ")}
              </span>
            </p>
          )}
        </section>

        {others.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
            <h2 className="mb-4 font-display text-xl font-semibold">More use cases</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((u: { slug: string; name: string; tagline: string }) => (
                <Link
                  key={u.slug}
                  to="/use-cases/$slug"
                  params={{ slug: u.slug }}
                  className="glass flex items-center gap-3 rounded-xl p-4 transition-colors hover:border-primary/40"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{u.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {u.tagline}
                    </span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
